import { uniqBy } from 'lodash/fp';

import { MATCH_REQUIREMENT_CHECK_NAMES } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/utils';
import ReactSelect from 'dashboard/components/ReactSelect';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import {
  CheckStub,
  MatchRequirementsCheckStub,
  MatchRequirementStub,
} from 'dashboard/models/simulate/stubs';
import {
  MatchGroupsCheckConfig,
  MatchRequirementsCheckConfig,
} from 'dashboard/models/verification-check';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';
import { VerificationStatus } from 'lib/verification';
import { CheckName } from 'lib/verificationCheck';

const FAILED_MATCH_REQUIREMENT_SELECT_PREFIX = 'failed_match_requirement_select';

const getMatchRequirementsOptions = (
  matchRequirementsCheckConfig: MatchRequirementsCheckConfig,
) => {
  return matchRequirementsCheckConfig['check-requirements'].map((matchRequirement) => ({
    value: matchRequirement.name as CheckName,
    label: matchRequirement.description,
  }));
};

const getMatchGroupsOptions = (matchGroupsCheckConfig: MatchGroupsCheckConfig) => {
  return uniqBy(
    'value',
    matchGroupsCheckConfig['match-groups'].flatMap((matchGroup) => {
      return matchGroup['match-requirements'].map((matchRequirement) => ({
        value: matchRequirement.name as CheckName,
        label: matchRequirement.description,
      }));
    }),
  );
};

interface MatchRequirementCheckStubsPanelProps {
  verificationTemplateVersion: VerificationTemplateVersion;
  checkStubs: (CheckStub | MatchRequirementsCheckStub)[];
  setCheckStubs: (checkStubs: (CheckStub | MatchRequirementsCheckStub)[]) => void;
  onOutsideClick: () => void;
  offsetElementId?: string;
  trackEvent: (eventName: string, props?: Record<string, any>) => void;
}

export const MatchRequirementCheckStubsPanel = ({
  verificationTemplateVersion,
  checkStubs,
  setCheckStubs,
  onOutsideClick,
  offsetElementId,
  trackEvent,
}: MatchRequirementCheckStubsPanelProps) => {
  const verificationChecksMap: Partial<Record<CheckName, VerificationCheckConfig>> =
    Object.fromEntries(
      verificationTemplateVersion.attributes['verification-checks'].map((verificationCheck) => [
        verificationCheck.check,
        verificationCheck,
      ]),
    );

  return (
    <MaximizablePanel
      title='Verification Checks'
      subtitle='Simulate verification outcomes by selecting verification check match requirements to fail.'
      onOutsideClickAndEscPress={onOutsideClick}
      offsetElementId={offsetElementId}
      outsideExceptionalIdPrefixes={[FAILED_MATCH_REQUIREMENT_SELECT_PREFIX]}
    >
      <Table>
        <TableHead sticky>
          <TableHeaderCell width='min'>Verification check</TableHeaderCell>
          <TableHeaderCell width='min'>Required</TableHeaderCell>
          <TableHeaderCell width='max'>Failed match requirements</TableHeaderCell>
        </TableHead>
        <TableBody>
          <>
            {verificationTemplateVersion.attributes['verification-checks']
              .filter((check) => MATCH_REQUIREMENT_CHECK_NAMES.includes(check.check))
              .map((check, idx) => {
                const checkStubIndex = checkStubs.findIndex(
                  (checkStub) => checkStub.name === check.check,
                );
                const checkStub =
                  checkStubIndex > -1
                    ? (checkStubs[checkStubIndex] as MatchRequirementsCheckStub)
                    : null;

                const isLegacy =
                  'check-requirements' in check &&
                  (check as MatchRequirementsCheckConfig)['check-requirements'].length > 0;

                return (
                  <TableRow key={idx}>
                    <TableCell>{CHECK_DISPLAY_CONFIG[check.check].name}</TableCell>
                    <TableCell>
                      {check['on-failed'] === VerificationStatus.Failed ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell>
                      <ReactSelect
                        id={`${FAILED_MATCH_REQUIREMENT_SELECT_PREFIX}_${idx}`}
                        isMulti
                        value={checkStub?.['match-requirement-stubs'].map(
                          (stub: MatchRequirementStub) => stub.name,
                        )}
                        options={
                          isLegacy
                            ? getMatchRequirementsOptions(
                                verificationChecksMap[check.check] as MatchRequirementsCheckConfig,
                              )
                            : getMatchGroupsOptions(
                                verificationChecksMap[check.check] as MatchGroupsCheckConfig,
                              )
                        }
                        onChange={(selectedFailedMatchRequirements: string[]) => {
                          const newMatchRequirementStubs = selectedFailedMatchRequirements.map(
                            (matchRequirementName) => ({
                              name: matchRequirementName,
                              status: 'failed',
                            }),
                          );

                          if (checkStub) {
                            const newCheckStubs = checkStubs.slice();
                            if (newMatchRequirementStubs.length > 0) {
                              newCheckStubs.splice(checkStubIndex, 1, {
                                ...checkStub,
                                'match-requirement-stubs': newMatchRequirementStubs,
                              });
                            } else {
                              newCheckStubs.splice(checkStubIndex, 1);
                            }
                            setCheckStubs(newCheckStubs);

                            return;
                          } else {
                            setCheckStubs([
                              ...checkStubs,
                              {
                                name: check.check,
                                'match-requirement-stubs': newMatchRequirementStubs,
                              },
                            ]);
                          }

                          trackEvent(
                            'Flow Editor - Preview Match Requirement Failed Requirement Selected',
                            {
                              failed_requirement:
                                selectedFailedMatchRequirements[
                                  selectedFailedMatchRequirements.length - 1
                                ],
                            },
                          );
                        }}
                        portal
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
          </>
        </TableBody>
      </Table>
    </MaximizablePanel>
  );
};
